import React, { useEffect, useState } from "react";
import Layout from "@/components/Layout";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Download, Users, GraduationCap, Award, TrendingUp, CheckCircle2 } from "lucide-react";

export default function CompanyAnalytics() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("learners");

  useEffect(() => {
    if (!user?.org_id) return;
    api.get(`/organizations/${user.org_id}/analytics`).then(r => setData(r.data)).catch(() => {});
  }, [user]);

  const exportCSV = async () => {
    const r = await api.get(`/organizations/${user.org_id}/analytics/export`, { responseType: "blob" });
    const url = URL.createObjectURL(r.data);
    const a = document.createElement("a");
    a.href = url;
    a.download = `forge-analytics-${user.org_id}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (!user?.org_id || user?.role !== "org_admin") {
    return <Layout><div className="px-10 py-20 overline">Org admin only.</div></Layout>;
  }
  if (!data) return <Layout><div className="px-10 py-20">Loading…</div></Layout>;

  const r = data.rollup;

  return (
    <Layout>
      <div className="px-10 py-10">
        <div className="flex items-end justify-between mb-10">
          <div>
            <div className="overline mb-2">Company tracking</div>
            <h1 className="editorial-h1 text-5xl">Team analytics.</h1>
          </div>
          <button onClick={exportCSV} data-testid="export-csv" className="btn-secondary"><Download size={14}/> Export CSV</button>
        </div>

        {/* Rollup stats */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-6 mb-10">
          <Stat label="Active learners" value={r.active_learners} icon={Users} testid="rollup-learners" />
          <Stat label="Enrollments" value={r.total_enrollments} icon={GraduationCap} testid="rollup-enrollments" />
          <Stat label="Completions" value={`${r.completions}`} sub={`${r.completion_rate}% rate`} icon={Award} testid="rollup-completions" />
          <Stat label="Pass rate" value={r.pass_rate !== null ? `${r.pass_rate}%` : "—"} sub={r.tests_passed + r.tests_failed > 0 ? `${r.tests_passed} pass · ${r.tests_failed} fail` : "no tests yet"} icon={CheckCircle2} testid="rollup-pass-rate" />
          <Stat label="Company score" value={r.company_score !== null ? `${r.company_score}` : "—"} sub="avg quiz score" icon={TrendingUp} testid="rollup-score" accent />
        </div>

        {/* Tabs */}
        <div className="flex gap-2 border-b border-[#E4E4E7] mb-6">
          {[
            { id: "learners", label: "By learner" },
            { id: "courses", label: "By course" },
          ].map(t => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              data-testid={`analytics-tab-${t.id}`}
              className={`px-5 py-3 font-display text-sm border-b-2 transition-colors ${tab === t.id ? "border-[#E63946] text-[#0A0A0A]" : "border-transparent text-[#52525B] hover:text-[#0A0A0A]"}`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {tab === "learners" ? (
          <div className="editorial-card-static overflow-x-auto">
            <table className="w-full text-sm" data-testid="learners-table">
              <thead className="bg-[#F9F9F8] text-left">
                <tr>
                  <Th>Learner</Th>
                  <Th>Email</Th>
                  <Th>Enrolled</Th>
                  <Th>Completed</Th>
                  <Th>Avg progress</Th>
                  <Th>Tests</Th>
                  <Th>Pass rate</Th>
                  <Th>Avg score</Th>
                  <Th>Certs</Th>
                </tr>
              </thead>
              <tbody>
                {data.learners.length === 0 ? (
                  <tr><td colSpan={9} className="px-5 py-8 text-center text-[#52525B]">No learner activity yet. Share your join links to get started.</td></tr>
                ) : data.learners.map(l => (
                  <tr key={l.user_id} className="border-t border-[#E4E4E7]" data-testid={`learner-row-${l.user_id}`}>
                    <Td><span className="font-display">{l.name}</span></Td>
                    <Td><span className="text-[#52525B]">{l.email}</span></Td>
                    <Td>{l.enrolled}</Td>
                    <Td>{l.completed}</Td>
                    <Td>
                      <div className="flex items-center gap-2">
                        <div className="w-24 h-1 bg-[#E4E4E7]"><div className="h-1 bg-[#0A0A0A]" style={{ width: `${l.avg_progress}%` }}/></div>
                        <span className="overline">{l.avg_progress}%</span>
                      </div>
                    </Td>
                    <Td>
                      {l.tests_taken > 0 ? (
                        <span className="overline">
                          <span className="text-[#166534]">{l.tests_passed} ✓</span>
                          {l.tests_failed > 0 && <span className="text-[#991B1B] ml-2">{l.tests_failed} ✗</span>}
                        </span>
                      ) : <span className="text-[#52525B]">—</span>}
                    </Td>
                    <Td>
                      {l.pass_rate !== null ? (
                        <span className={`overline ${l.pass_rate >= 80 ? "text-[#166534]" : l.pass_rate >= 50 ? "text-[#854D0E]" : "text-[#991B1B]"}`}>{l.pass_rate}%</span>
                      ) : <span className="text-[#52525B]">—</span>}
                    </Td>
                    <Td>{l.avg_score !== null ? `${l.avg_score}` : <span className="text-[#52525B]">—</span>}</Td>
                    <Td>{l.certs}</Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="editorial-card-static overflow-x-auto">
            <table className="w-full text-sm" data-testid="courses-table">
              <thead className="bg-[#F9F9F8] text-left">
                <tr>
                  <Th>Course</Th>
                  <Th>Category</Th>
                  <Th>Enrolled</Th>
                  <Th>Completed</Th>
                  <Th>Completion %</Th>
                  <Th>Passed / Failed</Th>
                  <Th>Pass rate</Th>
                  <Th>Avg score</Th>
                </tr>
              </thead>
              <tbody>
                {data.courses.length === 0 ? (
                  <tr><td colSpan={8} className="px-5 py-8 text-center text-[#52525B]">No course rollup yet.</td></tr>
                ) : data.courses.map(c => (
                  <tr key={c.course_id} className="border-t border-[#E4E4E7]" data-testid={`course-row-${c.course_id}`}>
                    <Td>
                      <div className="font-serif text-lg">{c.title}</div>
                      <div className="overline mt-1">pass ≥ {c.passing_score}%</div>
                    </Td>
                    <Td><span className="overline">{c.category}</span></Td>
                    <Td>{c.enrolled}</Td>
                    <Td>{c.completed}</Td>
                    <Td>
                      <div className="flex items-center gap-2">
                        <div className="w-20 h-1 bg-[#E4E4E7]"><div className="h-1 bg-[#E63946]" style={{ width: `${c.completion_rate}%` }}/></div>
                        <span className="overline">{c.completion_rate}%</span>
                      </div>
                    </Td>
                    <Td>
                      {c.tests_taken > 0 ? (
                        <span className="overline">
                          <span className="text-[#166534]">{c.tests_passed} ✓</span>
                          {c.tests_failed > 0 && <span className="text-[#991B1B] ml-2">{c.tests_failed} ✗</span>}
                          {c.not_tested > 0 && <span className="text-[#52525B] ml-2">· {c.not_tested} untested</span>}
                        </span>
                      ) : <span className="text-[#52525B]">— {c.not_tested > 0 ? `(${c.not_tested} untested)` : ""}</span>}
                    </Td>
                    <Td>
                      {c.pass_rate !== null ? (
                        <span className={`overline ${c.pass_rate >= 80 ? "text-[#166534]" : c.pass_rate >= 50 ? "text-[#854D0E]" : "text-[#991B1B]"}`}>{c.pass_rate}%</span>
                      ) : <span className="text-[#52525B]">—</span>}
                    </Td>
                    <Td>{c.avg_score !== null ? `${c.avg_score}` : <span className="text-[#52525B]">—</span>}</Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </Layout>
  );
}

const Th = ({ children }) => <th className="px-5 py-3 overline">{children}</th>;
const Td = ({ children }) => <td className="px-5 py-3">{children}</td>;

function Stat({ label, value, sub, icon: Icon, testid, accent }) {
  return (
    <div className={`editorial-card-static p-5 ${accent ? "bg-[#0A0A0A] text-white border-[#0A0A0A]" : ""}`} data-testid={testid}>
      <div className="flex items-start justify-between mb-4">
        <div className={`overline ${accent ? "text-[#E63946]" : ""}`}>{label}</div>
        <Icon size={16} strokeWidth={1.5}/>
      </div>
      <div className="font-display text-4xl font-bold">{value}</div>
      {sub && <div className={`overline mt-2 ${accent ? "text-zinc-500" : "text-[#52525B]"}`}>{sub}</div>}
    </div>
  );
}
